/* Página de producto Tanned & Co: galería, selector de packs, selector de tono,
   pestañas de «See your difference» y pasos de «How to apply». Sin dependencias. */
(() => {
  const ready = (fn) => (document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn) : fn());

  // Galería: las miniaturas mueven la foto grande; deslizar la foto actualiza la miniatura activa
  const initGallery = (root) => {
    const track = root.querySelector('[data-tcp-track]');
    const thumbs = [...root.querySelectorAll('[data-tcp-thumb]')];
    if (!track || track.dataset.ready) return;
    track.dataset.ready = '1';
    const slides = [...track.children];
    const mark = (i) => thumbs.forEach((t, k) => t.classList.toggle('is-on', k === i));
    thumbs.forEach((t) => t.addEventListener('click', () => {
      const i = Number(t.dataset.tcpThumb);
      track.scrollTo({ left: slides[i].offsetLeft - track.offsetLeft, behavior: 'smooth' });
      mark(i);
    }));
    track.addEventListener('scroll', () => requestAnimationFrame(() => {
      const i = Math.round(track.scrollLeft / track.clientWidth);
      mark(Math.min(i, slides.length - 1));
    }), { passive: true });
  };

  // Formato de precio igual al de la tienda (Settings → General → formato de moneda)
  const formatMoney = (cents, format) => {
    const v = cents / 100;
    const sep = (n, dec, th, ds) => {
      const [i, d] = n.toFixed(dec).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, th) + (d ? ds + d : '');
    };
    const out = {
      amount: sep(v, 2, ',', '.'),
      amount_no_decimals: sep(v, 0, ',', '.'),
      amount_with_comma_separator: sep(v, 2, '.', ','),
      amount_no_decimals_with_comma_separator: sep(v, 0, '.', ','),
      amount_with_apostrophe_separator: sep(v, 2, "'", '.'),
    };
    return (format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => out[k] ?? out.amount);
  };

  // Packs + cantidad + total. «Buy now» agrega y va directo al pago; «Add to cart» agrega y abre el carrito.
  const initOffers = (root) => {
    const form = root.querySelector('[data-tcp-form]');
    if (!form || form.dataset.ready) return;
    form.dataset.ready = '1';
    const radios = [...form.querySelectorAll('input[type="radio"][name="id"]')];
    const add = form.querySelector('[data-tcp-add]');
    const toCart = form.querySelector('[data-tcp-cart]');
    const soon = form.querySelector('[data-tcp-soon]');
    const qty = form.querySelector('[data-tcp-qty]');
    const minus = form.querySelector('[data-tcp-qty-minus]');
    const plus = form.querySelector('[data-tcp-qty-plus]');
    const total = form.querySelector('[data-tcp-total]');
    const max = Number(qty?.max) || 10;
    const getQty = () => Math.min(max, Math.max(1, parseInt(qty?.value, 10) || 1));
    // Si se llega desde la tarjeta de un kit (…?pack=the-glow-kit), ese pack viene elegido
    const wanted = new URLSearchParams(window.location.search).get('pack');
    const preset = wanted && radios.find((r) => r.dataset.handle === wanted);
    if (preset) preset.checked = true;
    // Precio en centavos: el del producto real, o el número escrito en el pack si todavía no existe en Shopify
    const centsOf = (r) => {
      const c = Number(r.dataset.cents);
      if (c > 0) return c;
      const m = String(r.dataset.price || '').match(/\d[\d,]*(?:\.\d+)?/);
      return m ? Math.round(parseFloat(m[0].replace(/,/g, '')) * 100) : 0;
    };

    const update = () => {
      const on = radios.find((r) => r.checked);
      radios.forEach((r) => r.closest('.tcp-offer').classList.toggle('is-on', r === on));
      const unavailable = !on || on.hasAttribute('data-unavailable');
      const n = getQty();
      if (qty) qty.value = n;
      if (minus) minus.disabled = n <= 1;
      if (plus) plus.disabled = n >= max;
      add.disabled = unavailable;
      if (toCart) toCart.disabled = unavailable;
      if (soon) soon.hidden = !unavailable;
      if (total) { const c = on ? centsOf(on) : 0; total.textContent = c ? formatMoney(c * n, form.dataset.moneyFormat) : ''; }
    };
    radios.forEach((r) => r.addEventListener('change', update));
    minus?.addEventListener('click', () => { qty.value = getQty() - 1; update(); });
    plus?.addEventListener('click', () => { qty.value = getQty() + 1; update(); });
    qty?.addEventListener('change', update);
    qty?.addEventListener('input', () => { if (qty.value !== '') update(); });

    form.addEventListener('submit', (e) => {
      if (add.disabled) { e.preventDefault(); return; }
      // «Add to cart»: envío normal del formulario a /cart/add → Shopify abre el carrito
      if (!e.submitter || !e.submitter.hasAttribute('data-tcp-buy')) return;
      // «Buy now»: agrega lo elegido al carrito y pasa directo al pago con todo el carrito.
      // (El enlace /cart/{id}:{cantidad} de Shopify reemplazaba el carrito y se perdía lo guardado.)
      e.preventDefault();
      const on = radios.find((r) => r.checked);
      const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      add.disabled = true;
      fetch(`${base}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(on.value), quantity: getQty() }] }),
      })
        .then((r) => { if (!r.ok) throw new Error(r.status); window.location.href = `${base}checkout`; })
        .catch(() => { add.disabled = false; form.submit(); });
    });
    update();
  };

  // «See your difference»: pestañas que llevan a cada antes/después y se marcan al deslizar
  const initDifference = (root) => {
    const rail = root.querySelector('[data-tcp-diff-rail]');
    if (!rail || rail.dataset.ready) return;
    rail.dataset.ready = '1';
    const tabs = [...root.querySelectorAll('[data-tcp-tab]')];
    const dots = [...root.querySelectorAll('[data-tcp-diff-dots] span')];
    const cards = [...rail.children];
    const center = (i) => rail.scrollTo({ left: cards[i].offsetLeft - (rail.clientWidth - cards[i].offsetWidth) / 2, behavior: 'smooth' });
    const mark = () => {
      const mid = rail.scrollLeft + rail.clientWidth / 2;
      let best = 0, dist = Infinity;
      cards.forEach((c, i) => { const d = Math.abs(c.offsetLeft + c.offsetWidth / 2 - mid); if (d < dist) { dist = d; best = i; } });
      cards.forEach((c, i) => c.classList.toggle('is-on', i === best));
      tabs.forEach((t, i) => t.setAttribute('aria-selected', String(i === best)));
      dots.forEach((d, i) => d.classList.toggle('is-on', i === best));
    };
    tabs.forEach((t, i) => t.addEventListener('click', () => center(i)));
    rail.addEventListener('scroll', () => requestAnimationFrame(mark), { passive: true });
    const start = Math.min(1, cards.length - 1);
    rail.scrollLeft = cards[start].offsetLeft - (rail.clientWidth - cards[start].offsetWidth) / 2;
    mark();
  };

  // «How to apply»: un paso a la vez, con barra de progreso
  const initSteps = (root) => {
    const box = root.querySelector('[data-tcp-steps]');
    if (!box || box.dataset.ready) return;
    box.dataset.ready = '1';
    const steps = [...box.querySelectorAll('[data-tcp-step]')];
    const bar = box.querySelector('[data-tcp-steps-bar]');
    const prev = box.querySelector('[data-tcp-steps-prev]');
    const next = box.querySelector('[data-tcp-steps-next]');
    let i = 0;
    const show = () => {
      steps.forEach((s, k) => { s.hidden = k !== i; });
      if (bar) bar.style.width = `${((i + 1) / steps.length) * 100}%`;
      prev.disabled = i === 0;
      next.disabled = i === steps.length - 1;
    };
    prev.addEventListener('click', () => { if (i > 0) { i -= 1; show(); } });
    next.addEventListener('click', () => { if (i < steps.length - 1) { i += 1; show(); } });
    show();
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-tcp-main]').forEach((s) => { initGallery(s); initOffers(s); });
    root.querySelectorAll('[data-tcp-difference]').forEach(initDifference);
    root.querySelectorAll('[data-tcp-howto]').forEach(initSteps);
  };
  ready(() => init());
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
